import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DecisionModel, Node, AllNode, AnyNode } from '../models/decision-model';

function isNodeObj(n: Node | any): n is { type: string } {
  return n && typeof n === 'object' && typeof n.type === 'string';
}
function isAllNode(n: unknown): n is AllNode {
  return isNodeObj(n) && (n as any).type === 'ALL' && Array.isArray((n as any).children);
}

function isAnyNode(n: unknown): n is AnyNode {
  return isNodeObj(n) && (n as any).type === 'ANY' && Array.isArray((n as any).children);
}

@Injectable({ providedIn: 'root' })
export class ModelBuilderService {
  private packMap: Record<string, string> = {
    'outpatient_allowed': 'assets/packs/pack.outpatient_allowed.json',
    'inpatient_only': 'assets/packs/pack.inpatient_only.json',
    'day_care_required': 'assets/packs/pack.day_care_required.json',
    'supply_any': 'assets/packs/pack.supply_any.json',
    'dose_cap_optional': 'assets/packs/pack.dose_cap_optional.json',
    'exclusions': 'assets/packs/pack.exclusions.json',
    'copay_exempt_icd': 'assets/packs/pack.copay_exempt_icd.json',

    // yeni eklediklerin varsa buraya ekle:
    'report_required': 'assets/packs/pack.report_required_basic.json',
    'report_optional': 'assets/packs/pack.report_optional.json',
    'prescriber_specialty_allow': 'assets/packs/pack.prescriber_specialty_allow.json',
    'age_range': 'assets/packs/pack.age_range.json',
    'gender_allow': 'assets/packs/pack.gender_allow.json',
    'prior_auth_required': 'assets/packs/pack.prior_auth_required.json',
    'icd_whitelist_or_prefix': 'assets/packs/pack.icd_whitelist_or_prefix.json'
  };

  constructor(private http: HttpClient) {}

  async build(overlayPath: string): Promise<DecisionModel> {
    const base = await this.http.get<DecisionModel>('assets/models/model.base.json').toPromise();
    const overlay = await this.http.get<any>(overlayPath).toPromise();
    if (!base || !overlay) throw new Error('Model/overlay bulunamadı');

    // Pack’leri yükle + parametreleri yerleştir
    const packs = await this.loadPacks(overlay.use_packs || []);
    const params = overlay.params || {};
    const instantiated = packs.map(p => this.instantiatePack(p, params));

    // copay_check düğümünü ayır (bulunamayabilir)
    const copayNode = instantiated.find(n => typeof n === 'object' && (n as any).id === 'copay_check') as Node | undefined;
    const booleanNodes = instantiated.filter(n => !(typeof n === 'object' && (n as any).id === 'copay_check')) as Node[];

    // base.meta ve (varsa) overlay.lists birleştir
    base.meta = { ...base.meta, ...overlay.meta };
    if (overlay.lists) {
      base.lists = { ...(base.lists || {}), ...(overlay.lists || {}) };
    }
    // overlay paramları UI için de dursun
    (base as any).__overlayParams = params;

    // Kök düğümü ALL/ANY değilse güvenli bir ALL “root” ile wrap et
    let root: Node | any = base.tree;
    if (!isAllNode(root) && !isAnyNode(root)) {
      root = {
        type: 'ALL',
        id: 'root',
        children: [],
        then: 'COVERED',
        else: 'NOT_COVERED'
      };
      base.tree = root;
    }

    // Artık children/then atanabilir
    if (isAllNode(base.tree) || isAnyNode(base.tree)) {
      // children
      base.tree.children = booleanNodes;

      // then: copay bloğu varsa onu, yoksa 'COVERED'
      // (ALL/ANY BaseNode’da then opsiyoneldir; burada set ediyoruz)
      (base.tree as any).then = copayNode ?? 'COVERED';

      // else kökte zaten base.model.base.json’da 'NOT_COVERED' tanımlı, dokunmuyoruz
    } else {
      // Teoride buraya düşmemeli; yine de emniyet için
      base.tree = {
        type: 'ALL',
        id: 'root',
        children: booleanNodes,
        then: copayNode ?? 'COVERED',
        else: 'NOT_COVERED'
      } as any;
    }

    return base;
  }

  private async loadPacks(names: string[]): Promise<Node[]> {
    const reqs = names.map(n => {
      const path = this.packMap[n];
      if (!path) throw new Error(`Pack bulunamadı: ${n}`);
      return this.http.get<Node>(path).toPromise();
    });
    return Promise.all(reqs);
  }

  private instantiatePack(pack: Node, params: Record<string, any>): Node {
    const clone = JSON.parse(JSON.stringify(pack));
    const sub = (s: string) =>
      s.replace(/\$\{([A-Z0-9_]+)\}/g, (_m, k) => {
        const v = params[k];
        if (v === undefined || v === null) return '';
        if (Array.isArray(v)) return JSON.stringify(v);
        return String(v);
      });

    const walk = (o: any) => {
      if (o && typeof o === 'object') {
        for (const k of Object.keys(o)) {
          const v = o[k];
          if (typeof v === 'string') o[k] = sub(v);
          else walk(v);
        }
      }
    };
    walk(clone);

    // indication_any gibi özel üretimler varsa burada ele al (örnek bıraktım)
    if ((clone as any).id === 'indication_any' && (!('children' in clone) || !(clone as any).children?.length)) {
      const arr = params.INDICATIONS as string[] | undefined;
      if (Array.isArray(arr)) {
        (clone as any).children = arr.map(val => ({
          type: 'CLAUSE',
          cond: `claim.indication == '${val}'`
        }));
      }
    }

    return clone;
  }
}
